import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserStatusPill } from '@/components/AdminUsers/UserStatusPill';
import { UserActionButtons } from '@/components/AdminUsers/UserActionButtons';
import { formatUserDate, getUserStatus } from '@/utils/adminUserStatus';
import type { AdminUser, AdminUserActionHandlers } from '@/types/adminUsers';
import styles from './styles.module.scss';

export interface UserListItemProps extends AdminUserActionHandlers {
  user: AdminUser;
}

/** Mobile row card for /admin/users: identity, status, dates and actions. */
export const UserListItem: React.FC<UserListItemProps> = ({ user, ...actionHandlers }) => {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.language;

  const identity =
    user.email ||
    (user.username ? `@${user.username}` : t('admin:userTelegramId', { id: user.telegram_id }));

  const created = t('admin:userCreated', {
    date: formatUserDate(user.created_at, locale, { shortYear: true }),
  });
  const lastLogin = t('admin:userLastLogin', {
    date: user.last_login
      ? formatUserDate(user.last_login, locale, { withTime: true, shortYear: true })
      : t('common:never'),
  });

  return (
    <article className={styles.item} data-testid={`admin-user-${user.id}`}>
      <div className={styles.avatar} aria-hidden="true">
        {user.first_name?.[0]?.toUpperCase() || '?'}
      </div>

      <div className={styles.body}>
        <div className={styles.head}>
          <span className={styles.name}>
            {user.first_name} {user.last_name || ''}
          </span>
          <UserStatusPill status={getUserStatus(user)} />
        </div>
        <div className={styles.identity}>{identity}</div>
        <div className={styles.meta}>
          <span className={styles.metaChunk}>{created}</span>
          <span className={styles.metaSeparator} aria-hidden="true">
            ·
          </span>
          <span className={styles.metaChunk}>{lastLogin}</span>
        </div>
        {user.blocked_reason && (
          <div className={styles.reason}>
            {t('admin:userBlockedReason', { reason: user.blocked_reason })}
          </div>
        )}
      </div>

      <UserActionButtons user={user} className={styles.actions} {...actionHandlers} />
    </article>
  );
};

export default UserListItem;
